<div 
  class="page min-h-screen bg-white font-sans text-gray-900 selection:bg-orange-100 selection:text-brand flex flex-col relative overflow-hidden"
  data-testid="wizard-profile-section"
>

  <!-- Background Decor -->
  <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
  <div class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none -translate-x-1/3 translate-y-1/3"></div>

  <!-- Header / Logo -->
  <div class="w-full max-w-5xl mx-auto px-6 py-8 relative z-10">
    <a routerLink="/wizard/v2/landing" class="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="wizard-profile-logo">
      <img src="images/logo.png" alt="JustTeach Logo" class="h-8 w-auto"/>
    </a>
  </div>

  <!-- Main Content -->
  <div class="flex-1 flex items-center justify-center px-4 pb-12 relative z-10">
    <div class="max-w-2xl w-full">

      <!-- Stepper -->
      <div class="mb-12" data-testid="wizard-profile-stepper">
        <div class="flex items-start justify-center space-x-4">
          <!-- Step 1: Active -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-profile-stepper-step-0">
            <div class="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-brand/20">
              1
            </div>
            <span class="font-bold text-sm text-brand">Profile</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-gray-200 rounded-full mt-4"></div>

          <!-- Step 2: Pending -->
          <div class="flex flex-col items-center gap-2 opacity-50" data-testid="wizard-profile-stepper-step-1">
            <div class="w-8 h-8 rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex items-center justify-center font-bold text-sm">
              2
            </div>
            <span class="font-medium text-sm text-gray-500">School</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-gray-200 rounded-full mt-4"></div>

          <!-- Step 3: Pending -->
          <div class="flex flex-col items-center gap-2 opacity-50" data-testid="wizard-profile-stepper-step-2">
            <div class="w-8 h-8 rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex items-center justify-center font-bold text-sm">
              3
            </div>
            <span class="font-medium text-sm text-gray-500">Personalize</span>
          </div>
        </div>
      </div>

      <div class="text-center mb-10">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4" data-testid="wizard-profile-heading">
          Tell us more about you
        </h1>
        <p class="text-gray-600 text-lg max-w-lg mx-auto leading-relaxed" data-testid="wizard-profile-subheading">
          We personalize your AI models based on your profile. <br class="hidden sm:block"/>
          This helps us generate standard-aligned content instantly.
        </p>
      </div>

      <div class="bg-white/80 backdrop-blur-xl border border-white shadow-2xl shadow-gray-200/50 rounded-2xl p-6 sm:p-10" data-testid="wizard-profile-form-container">
        <form [formGroup]="profileForm" (ngSubmit)="onSubmit()" class="space-y-8" data-testid="wizard-profile-form">

          <!-- Name Section -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="wizard-profile-name-section">
            <div class="space-y-3" data-testid="wizard-profile-firstname-field">
              <label class="block text-sm font-bold text-gray-900" data-testid="wizard-profile-firstname-label">
                First name <span class="text-brand">*</span>
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <lucide-icon [img]="icons.User" class="w-5 h-5"></lucide-icon>
                </div>
                <input
                  type="text"
                  formControlName="firstName"
                  class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  [class.border-red-400]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
                  [class.focus:border-red-400]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
                  [class.focus:ring-red-400/20]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
                  placeholder="Jane"
                  data-testid="wizard-profile-firstname-input"
                />
              </div>
              @if (profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched) {
                <p class="text-sm text-red-500 font-medium" data-testid="wizard-profile-firstname-error">
                  Please enter your first name
                </p>
              }
            </div>
            <div class="space-y-3" data-testid="wizard-profile-lastname-field">
              <label class="block text-sm font-bold text-gray-900" data-testid="wizard-profile-lastname-label">
                Last name
              </label>
              <input
                type="text"
                formControlName="lastName"
                class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                placeholder="Doe"
                data-testid="wizard-profile-lastname-input"
              />
            </div>
          </div>

          <!-- Grades Section -->
          <div class="space-y-3" data-testid="wizard-profile-grades-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-profile-grades-label">
              Grades <span class="text-brand">*</span>
            </label>
            <div class="flex flex-wrap gap-2.5" data-testid="wizard-profile-grades-list">
              @for (grade of grades; track grade; let i = $index) {
                <button
                  type="button"
                  (click)="toggleGrade(grade)"
                  class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
                  [class.bg-brand]="isGradeSelected(grade)"
                  [class.text-white]="isGradeSelected(grade)"
                  [class.border-brand]="isGradeSelected(grade)"
                  [class.shadow-md]="isGradeSelected(grade)"
                  [class.shadow-brand/30]="isGradeSelected(grade)"
                  [class.bg-white]="!isGradeSelected(grade)"
                  [class.text-gray-600]="!isGradeSelected(grade)"
                  [class.border-gray-200]="!isGradeSelected(grade)"
                  [class.hover:border-gray-300]="!isGradeSelected(grade)"
                  [class.hover:bg-gray-50]="!isGradeSelected(grade)"
                  [attr.data-testid]="'wizard-profile-grade-' + i"
                >
                  {{ grade }}
                </button>
              }
            </div>
            <p class="text-xs text-gray-500 mt-1" data-testid="wizard-profile-grades-hint">Select all that apply.</p>
            @if (selectedGrades().length === 0 && profileForm.touched) {
              <p class="text-sm text-red-500 font-medium" data-testid="wizard-profile-grades-error">
                Please select at least one grade
              </p>
            }
          </div>

          <!-- Subject Section -->
          <div class="space-y-3" data-testid="wizard-profile-subjects-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-profile-subjects-label">
              Subject <span class="text-brand">*</span>
            </label>
            <div class="flex flex-wrap gap-2.5" data-testid="wizard-profile-subjects-list">
              @for (subject of subjects; track subject; let i = $index) {
                <button
                  type="button"
                  (click)="toggleSubject(subject)"
                  class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer flex items-center gap-1.5"
                  [class.bg-brand]="isSubjectSelected(subject)"
                  [class.text-white]="isSubjectSelected(subject)"
                  [class.border-brand]="isSubjectSelected(subject)"
                  [class.shadow-md]="isSubjectSelected(subject)"
                  [class.shadow-brand/30]="isSubjectSelected(subject)"
                  [class.bg-white]="!isSubjectSelected(subject)"
                  [class.text-gray-600]="!isSubjectSelected(subject)"
                  [class.border-gray-200]="!isSubjectSelected(subject)"
                  [class.hover:border-gray-300]="!isSubjectSelected(subject)"
                  [class.hover:bg-gray-50]="!isSubjectSelected(subject)"
                  [attr.data-testid]="'wizard-profile-subject-' + i"
                >
                  <lucide-icon [img]="subjectIcons[subject]" class="w-4 h-4"></lucide-icon>
                  {{ subject }}
                </button>
              }
            </div>
            <p class="text-xs text-gray-500 mt-1" data-testid="wizard-profile-subjects-hint">Select all that apply.</p>
            @if (selectedSubjects().length === 0 && profileForm.touched) {
              <p class="text-sm text-red-500 font-medium" data-testid="wizard-profile-subjects-error">
                Please select at least one subject
              </p>
            }
          </div>

          <!-- Role Section -->
          <div class="space-y-3" data-testid="wizard-profile-role-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-profile-role-label">
              What's your role at the school? <span class="text-brand">*</span>
            </label>
            <!-- Hidden form control -->
            <input type="hidden" formControlName="role" />
            <div class="flex flex-wrap gap-2.5" data-testid="wizard-profile-roles-list">
              @for (role of roles; track role; let i = $index) {
                <button
                  type="button"
                  (click)="selectRole(role)"
                  class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer flex items-center gap-1.5"
                  [class.bg-brand]="isRoleSelected(role)"
                  [class.text-white]="isRoleSelected(role)"
                  [class.border-brand]="isRoleSelected(role)"
                  [class.shadow-md]="isRoleSelected(role)"
                  [class.shadow-brand/30]="isRoleSelected(role)"
                  [class.bg-white]="!isRoleSelected(role)"
                  [class.text-gray-600]="!isRoleSelected(role)"
                  [class.border-gray-200]="!isRoleSelected(role)"
                  [class.hover:border-gray-300]="!isRoleSelected(role)"
                  [class.hover:bg-gray-50]="!isRoleSelected(role)"
                  [attr.data-testid]="'wizard-profile-role-' + i"
                >
                  <lucide-icon [img]="roleIcons[role]" class="w-4 h-4"></lucide-icon>
                  {{ role }}
                </button>
              }
            </div>
            @if (profileForm.get('role')?.invalid && profileForm.get('role')?.touched) {
              <p class="text-sm text-red-500 font-medium" data-testid="wizard-profile-role-error">Please select your role</p>
            }
          </div>

          <div class="pt-4">
            <button
              type="submit"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 group text-lg disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              [disabled]="isLoading$ | async"
              data-testid="wizard-profile-submit-btn"
            >
              @if (isLoading$ | async) {
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              } @else {
                Continue
                <lucide-icon [img]="icons.ArrowRight" class="w-5 h-5 group-hover:translate-x-1 transition-transform"></lucide-icon>
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  </div>
</div>
